import React from "react";
import { useAppSelector } from "index";
import { Grid, Box, Paper, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ApplicationState } from "store/index";
import ThemeSwitch from "containers/NavbarComponents/ThemeSwitch";
import LanguageSelect from "containers/NavbarComponents/LanguageSelect";
import useTranslations from "containers/hooks/useLanguageHook";
import MyDropzone from "containers/Components/Dropzone";
import { getObjectAsString } from "utils/array";

function DebugPage() {
  const themetype = useAppSelector((state: ApplicationState) => state.applicationLayout.type);
  const files = useAppSelector((state: ApplicationState) => state.fileParsing.file);
  const lastFilename = useAppSelector((state: ApplicationState) => state.fileParsing.lastFilename);
  const { t, lang } = useTranslations();
  const state = useAppSelector((state: ApplicationState) => state);
  const theme = useTheme();

  return (
    <Box
      sx={{
        width: "100%",
        display: "block",
        boxSizing: "border-box",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "960px",
        paddingTop: "48px",
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <h1>Debug Page</h1>
          <Typography>For debug stuff and testing</Typography>
        </Grid>

        <Grid item xs={12} container justifyContent="center" spacing={2}>
          <Grid item>
            <Button variant="contained" onClick={() => console.log(state)}>
              {" "}
              Log AppState to Console{" "}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={() => console.log(files)}>
              {" "}
              Log Files to Console{" "}
            </Button>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <h1>Language</h1>
          <Typography>Current language: {lang} </Typography>
          <Typography>{t.name} </Typography>
          <Typography>{t.type} </Typography>
          <Typography>{t.language} </Typography>
          <LanguageSelect />
          <Paper sx={{ height: 300, overflow: "scroll" }}>
            <Typography variant="code">{getObjectAsString(t)}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <h1>Worker Status</h1>
          <MyDropzone />
          <Typography>Files Status: {files.map((d) => `${d.file.name} (${d.progress}%)`).join(", ")} </Typography>
          <Typography>Last Filename: {lastFilename} </Typography>
          <Typography>
            File Error:{" "}
            {files
              .filter(({ error }) => error !== null)
              .map((d) => `${d.file.name} (${d.error})`)
              .join(", ")}{" "}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <h1>Theme Color</h1>
          <Typography>Current theme type: {themetype} </Typography>
          <ThemeSwitch />
          {renderColors()}
        </Grid>

        <Grid item xs={12}>
          <h1>Typography</h1>
          {renderTexts()}
        </Grid>

        <Grid item xs={12}>
          <h1>Backgrounds</h1>
          {renderBackgrounds()}
        </Grid>
      </Grid>
    </Box>
  );

  function renderTexts() {
    return (
      <Paper>
        <Typography variant="h1" component="h2" gutterBottom>
          h1. Heading
        </Typography>
        <Typography variant="h2" gutterBottom>
          h2. Heading
        </Typography>
        <Typography variant="h3" gutterBottom>
          h3. Heading
        </Typography>
        <Typography variant="h4" gutterBottom>
          h4. Heading
        </Typography>
        <Typography variant="h5" gutterBottom>
          h5. Heading
        </Typography>
        <Typography variant="h6" gutterBottom>
          h6. Heading
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          subtitle1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          subtitle2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur
        </Typography>
        <Typography variant="body1" gutterBottom>
          body1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur unde suscipit, quam
          beatae rerum inventore consectetur, neque doloribus, cupiditate numquam dignissimos laborum fugiat deleniti?
          Eum quasi quidem quibusdam.
        </Typography>
        <Typography variant="body2" gutterBottom>
          body2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur unde suscipit, quam
          beatae rerum inventore consectetur, neque doloribus, cupiditate numquam dignissimos laborum fugiat deleniti?
          Eum quasi quidem quibusdam.
        </Typography>
        <Typography variant="button" display="block" gutterBottom>
          button text
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          caption text
        </Typography>
        <Typography variant="overline" display="block" gutterBottom>
          overline text
        </Typography>
      </Paper>
    );
  }

  function renderBackgrounds() {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="primary.main" color="primary.contrastText" p={2}>
            primary.main
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="secondary.main" color="secondary.contrastText" p={2}>
            secondary.main
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="error.main" color="error.contrastText" p={2}>
            error.main
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="warning.main" color="warning.contrastText" p={2}>
            warning.main
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="info.main" color="info.contrastText" p={2}>
            info.main
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="success.main" color="success.contrastText" p={2}>
            success.main
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="text.primary" color="background.paper" p={2}>
            text.primary
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="text.secondary" color="background.paper" p={2}>
            text.secondary
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box bgcolor="text.disabled" color="background.paper" p={2}>
            text.disabled
          </Box>
        </Grid>
      </Grid>
    );
  }

  function renderColors() {
    return (
      <Grid container direction="column" spacing={1}>
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={4}>
            {createRect(theme.palette.background.default)} background.default
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.background.paper)} background.paper
          </Grid>
        </Grid>
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={4}>
            {createRect(theme.palette.primary.light)} primary.light
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.primary.main)} primary.main
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.primary.dark)} primary.dark
          </Grid>
        </Grid>
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={4}>
            {createRect(theme.palette.secondary.light)} secondary.light
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.secondary.main)} secondary.main
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.secondary.dark)} secondary.dark
          </Grid>
        </Grid>
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={4}>
            {createRect(theme.palette.success.light)} success.light
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.success.main)} success.main
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.success.dark)} success.dark
          </Grid>
        </Grid>
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={4}>
            {createRect(theme.palette.info.light)} info.light
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.info.main)} info.main
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.info.dark)} info.dark
          </Grid>
        </Grid>
        <Grid item container direction="row" spacing={1}>
          <Grid item xs={4}>
            {createRect(theme.palette.error.light)} error.light
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.error.main)} error.main
          </Grid>
          <Grid item xs={4}>
            {createRect(theme.palette.error.dark)} error.dark
          </Grid>
        </Grid>
        <Grid item ml={2}>
          <h1>Categories</h1>
        </Grid>
        {Object.entries(theme.palette.categories).map(([key, d]) => (
          <Grid item container direction="row" spacing={1} key={key}>
            <Grid item xs={4}>
              {createRect(d.light)} categories.{key}.light
            </Grid>
            <Grid item xs={4}>
              {createRect(d.main)} categories.{key}.main
            </Grid>
            <Grid item xs={4}>
              {createRect(d.dark)} categories.{key}.dark
            </Grid>
          </Grid>
        ))}
        <Grid item ml={2}>
          <h1>Files</h1>
        </Grid>
        {Object.entries(theme.palette.files).map(([key, d]) => (
          <Grid item container direction="row" spacing={1} key={key}>
            <Grid item xs={4}>
              {createRect(d.light)} files.{key}.light
            </Grid>
            <Grid item xs={4}>
              {createRect(d.main)} files.{key}.main
            </Grid>
            <Grid item xs={4}>
              {createRect(d.dark)} files.{key}.dark
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }

  function createRect(color: string) {
    return (
      <Box
        sx={{
          width: "48px",
          height: "48px",
          marginRight: "8px",
          borderRadius: "4px",
          float: "left",
          backgroundColor: color,
        }}
      />
    );
  }
}

export { DebugPage };
